"use client";
import React from "react";
import { Box, Container, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import Image from "next/image";
import { useTranslation } from "react-i18next";

// ─── Assets ─────────────────────────────────────────────────────────────────
const BG_IMG = "https://getgeek-website-assets.b-cdn.net/BGimageAutonomous.webp";
// const RIGHT_IMG = "https://getgeek-website-assets.b-cdn.net/AutonomousRightImg.webp";
const BG_IMG_AR = "https://getgeek-website-assets.b-cdn.net/Getgeek%20Images/BGimageAutonomousArabic.webp";
const RIGHT_IMG = "https://getgeek-website-assets.b-cdn.net/Getgeek%20Images/AutonomousRightImgTwo.webp";

// ─── Feature Cards Data ───────────────────────────────────────────────────────
const FEATURES = [
  { translationKey: "leapAutonomousSection.features.meetZayn" },
  { translationKey: "leapAutonomousSection.features.smartScreening" },
  { translationKey: "leapAutonomousSection.features.zeroBias" },
  { translationKey: "leapAutonomousSection.features.worksWithStack" },
  { translationKey: "leapAutonomousSection.features.honestInterviews" },
  { translationKey: "leapAutonomousSection.features.realTimeAnalytics" },
];

// ─── Styled Components ──────────────────────────────────────────────────────
const AutonomousSection = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isRTL",
})(({ theme, isRTL }) => ({
  position: "relative",
  backgroundImage: `url(${isRTL ? BG_IMG_AR : BG_IMG})`,
  backgroundSize: "cover",
  backgroundPosition: "center",
  backgroundRepeat: "no-repeat",
  backgroundColor: "#0B0715",
  padding: "72px 0",
  [theme.breakpoints.down("sm")]: {
    padding: "48px 0",
  },
}));

const TopRow = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: "40px",
  marginBottom: "48px",
  [theme.breakpoints.down("md")]: {
    flexDirection: "column",
    gap: "32px",
  },
}));

const LeftContent = styled(Box)(({ theme }) => ({
  flex: "0 0 46%",
  maxWidth: "46%",
  [theme.breakpoints.down("md")]: {
    flex: "0 0 100%",
    maxWidth: "100%",
  },
}));

const RightImageWrapper = styled(Box)(({ theme }) => ({
  flex: "0 0 48%",
  maxWidth: "480px",
  marginInlineStart: "auto",
  lineHeight: 0,
  [theme.breakpoints.down("md")]: {
    flex: "0 0 100%",
    maxWidth: "100%",
    marginInlineStart: 0,
  },
}));

const Eyebrow = styled(Typography, {
  shouldForwardProp: (prop) => prop !== "isRTL",
})(({ theme, isRTL }) => ({
  display: "flex",
  alignItems: "center",
  gap: "8px",
  fontFamily: isRTL ? "var(--font-outfit)" : "var(--font-outfit)",
  fontSize: "14px",
  fontWeight: 700,
  letterSpacing: "1px",
  textTransform: "uppercase",
  color: "#9048FB",
  marginBottom: "16px",
  [theme.breakpoints.down("sm")]: {
    fontSize: "12px",
  },

  "&::before": {
    content: '""',
    display: "inline-block",
    width: "6px",
    height: "6px",
    borderRadius: "50%",
    backgroundColor: "#A78BFA",
  },
}));

const SectionHeading = styled(Typography, {
  shouldForwardProp: (prop) => prop !== "isRTL",
})(({ theme, isRTL }) => ({
  fontFamily: isRTL ? "var(--font-plusjakarta)" : "var(--font-plusjakarta)",
  fontWeight: 700,
  fontSize: "42px",
  lineHeight: 1.3,
  color: "#FFFFFF",
  marginBottom: "16px",
  [theme.breakpoints.down("sm")]: {
    fontSize: "25px",
  },
}));

const SectionParagraph = styled(Typography, {
  shouldForwardProp: (prop) => prop !== "isRTL",
})(({ isRTL }) => ({
  fontFamily: isRTL ? "var(--font-outfit)" : "var(--font-outfit)",
  fontSize: "16px",
  lineHeight: 1.8,
  color: "#B8B4C4",
  maxWidth: "660px",
}));

const FeaturesGrid = styled(Box)(({ theme }) => ({
  display: "flex",
  flexWrap: "wrap",
  gap: "16px",
  [theme.breakpoints.down("md")]: {
    flexDirection: "column",
  },
}));

const FeatureCard = styled(Box)(({ theme }) => ({
  flex: "1 1 calc(33.333% - 16px)",
  backgroundColor: "rgba(255,255,255,0.03)",
  border: "1px solid rgba(255,255,255,0.08)",
  borderRadius: "12px",
  padding: "22px",
  [theme.breakpoints.down("md")]: {
    flex: "1 1 100%",
  },
}));

const FeatureTitleRow = styled(Box)(() => ({
  display: "flex",
  alignItems: "center",
  gap: "10px",
  marginBottom: "10px",
}));

const Diamond = styled(Box)(() => ({
  width: "8px",
  height: "8px",
  flexShrink: 0,
  backgroundColor: "#9048FB",
  transform: "rotate(45deg)",
}));

const FeatureTitle = styled(Typography, {
  shouldForwardProp: (prop) => prop !== "isRTL",
})(({ isRTL }) => ({
  fontFamily: isRTL ? "var(--font-outfit)" : "var(--font-outfit)",
  fontWeight: 700,
  fontSize: "16px",
  color: "#FFFFFF",
}));

const FeatureDescription = styled(Typography, {
  shouldForwardProp: (prop) => prop !== "isRTL",
})(({ isRTL }) => ({
  fontFamily: isRTL ? "var(--font-outfit)" : "var(--font-outfit)",
  fontSize: "13px",
  lineHeight: 1.7,
  color: "#A6A2B3",
}));

// ─── Component ───────────────────────────────────────────────────────────────
const LeapAutonomousSection = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === "ar";

  return (
  <AutonomousSection isRTL={isRTL}>
      <Container maxWidth="xl">
        {/* Top: heading + image */}
        <TopRow>
          <LeftContent>
            <Eyebrow isRTL={isRTL}>
              {t("leapAutonomousSection.eyebrow")}
            </Eyebrow>

            <SectionHeading variant="h2" isRTL={isRTL}>
              {t("leapAutonomousSection.heading")}
            </SectionHeading>

            <SectionParagraph isRTL={isRTL}>
              {t("leapAutonomousSection.description")}
            </SectionParagraph>
          </LeftContent>

          <RightImageWrapper>
            <Image
              src={RIGHT_IMG}
              alt="Getgeek AI Agents"
              width={520}
              height={420}
              style={{
                width: "100%",
                height: "auto",
                display: "block",
                objectFit: "contain",
              }}
            />
          </RightImageWrapper>
        </TopRow>

        {/* Feature cards grid */}
        <FeaturesGrid>
          {FEATURES.map((feature) => (
            <FeatureCard key={feature.translationKey}>
              <FeatureTitleRow>
                <Diamond />
                <FeatureTitle isRTL={isRTL}>
                  {t(`${feature.translationKey}.title`)}
                </FeatureTitle>
              </FeatureTitleRow>
              <FeatureDescription isRTL={isRTL}>
                {t(`${feature.translationKey}.description`)}
              </FeatureDescription>
            </FeatureCard>
          ))}
        </FeaturesGrid>
      </Container>
    </AutonomousSection>
  );
};

export default LeapAutonomousSection;